import { GAME_CONSTANTS } from '../GameConstants.js';
import DebugHelper from '../DebugHelper.js';
import GameStateStore from '../GameStateStore.js';
import { publishCombatState } from '../CombatStateSync.js';
import IdGenerator from '../utils/IdGenerator.js';
import { ensureAudios, playSceneSfx } from '../LazyAssetLoader.js';
import { getEncounterArea } from '../SceneClassHelper.js';

export default class WorldEncounterSystem {
    constructor(scene) {
        this.scene = scene;
        
        if (GameStateStore.get('lastEncounterTime') === undefined) {
            GameStateStore.setLastEncounterTime(0);
        }
        
        this.encounterCooldown = GAME_CONSTANTS.ENCOUNTER.COOLDOWN;
        this.encounterChance = GAME_CONSTANTS.ENCOUNTER.BASE_CHANCE;
        this.enemies = this.scene.cache.json.get('enemies');
        
        this.isTriggeringBattle = false;
        this._fixedBattleIntroPending = false;
        this._fixedBattleIntroTimer = null;
        this._fixedBattleSkipHandler = null;
    }
    
    _clearFixedBattleIntro() {
        if (this._fixedBattleIntroTimer) {
            this._fixedBattleIntroTimer.remove(false);
            this._fixedBattleIntroTimer = null;
        }
        if (this._fixedBattleSkipHandler && this.scene.input?.keyboard) {
            this.scene.input.keyboard.off('keydown', this._fixedBattleSkipHandler);
        }
        this._fixedBattleSkipHandler = null;
        this._fixedBattleIntroPending = false;
        this.dismissBattleIntroMessage();
    }

    /**
     * 关闭固定遇敌前的提示消息框
     */
    dismissBattleIntroMessage() {
        if (this.scene.worldUI?.isDialogActive) {
            this.scene.worldUI.hideDialog();
        }
    }
    
    _launchFixedBattleScene(battleData) {
        const existingBattleScene = this.scene.scene.get('BattleScene');
        if (existingBattleScene && existingBattleScene.scene && existingBattleScene.scene.isActive()) {
            this.isTriggeringBattle = false;
            return;
        }
        
        try {
            this.dismissBattleIntroMessage();

            if (this.scene.cameras && this.scene.cameras.main) {
                this.scene.cameras.main.setVisible(false);
            }
            
            const currentSceneKey = this.scene.scene.key;
            this.scene.movementEnabled = false;
            this.scene.scene.pause(currentSceneKey);
            this.scene.scene.launch('BattleScene', battleData);
            
            this.scene.time.delayedCall(100, () => {
                const battleScene = this.scene.scene.get('BattleScene');
                if (battleScene && battleScene.scene && battleScene.scene.isActive() && battleScene.cameras && battleScene.cameras.main) {
                    battleScene.cameras.main.setVisible(true);
                }
                this.isTriggeringBattle = false;
            });
        } catch (error) {
            DebugHelper.error('启动战斗场景失败', error);
            this.isTriggeringBattle = false;
        }
    }
    
    _beginFixedBattleIntro(battleData) {
        this._clearFixedBattleIntro();
        this._fixedBattleIntroPending = true;
        
        const launchNow = () => {
            if (!this._fixedBattleIntroPending) {
                return;
            }
            this._clearFixedBattleIntro();
            this._launchFixedBattleScene(battleData);
        };
        
        this._fixedBattleIntroTimer = this.scene.time.delayedCall(
            GAME_CONSTANTS.BATTLE.FIXED_BATTLE_INTRO_MS,
            launchNow
        );
        
        this._fixedBattleSkipHandler = (event) => {
            if (event.code !== 'Space') {
                return;
            }
            launchNow();
        };
        this.scene.input.keyboard.on('keydown', this._fixedBattleSkipHandler);
        
        if (this.scene.worldUI) {
            this.scene.worldUI.showMessage('遭遇敌人！（空格跳过）');
        }
    }
    
    getMovementSpeedSq() {
        const sprite = this.scene.inVehicle && this.scene.vehicleSprite
            ? this.scene.vehicleSprite
            : this.scene.playerSprite;

        if (!sprite || !sprite.body) {
            return 0;
        }

        const velocity = sprite.body.velocity;
        if (!velocity) return 0;

        return velocity.x * velocity.x + velocity.y * velocity.y;
    }
    
    checkRandomEncounter() {
        if (!this.scene.playerSprite || this.scene.worldUI.isDialogActive) {
            return;
        }

        const speedSq = this.getMovementSpeedSq();
        const minSpeedSq = GAME_CONSTANTS.ENCOUNTER.MIN_SPEED_FOR_ENCOUNTER ** 2;
        const isMoving = speedSq > minSpeedSq;
        
        if (!isMoving) {
            return;
        }

        const now = Date.now();
        const encounterConfig = this.getEncounterConfig();
        const cooldown = encounterConfig.encounterCooldown ?? this.encounterCooldown;
        if (now - (GameStateStore.get('lastEncounterTime') || 0) < cooldown) {
            return;
        }

        const currentSpeed = Math.sqrt(speedSq);
        const baseChance = encounterConfig.encounterRate ?? this.encounterChance;
        let adjustedChance = baseChance;
        if (currentSpeed > GAME_CONSTANTS.ENCOUNTER.SPEED_THRESHOLD) {
            adjustedChance = baseChance * (currentSpeed / GAME_CONSTANTS.ENCOUNTER.SPEED_THRESHOLD);
        }
        
        if (this.scene.inVehicle) {
            adjustedChance *= GAME_CONSTANTS.ENCOUNTER.VEHICLE_CHANCE_MULTIPLIER;
        }
        
        if (Math.random() < adjustedChance) {
            GameStateStore.setLastEncounterTime(now);

            if (this.scene.bgm) {
                this.scene.bgm.stop();
            }
            ensureAudios(this.scene, ['encounter_bgm']).then(() => {
                this.scene.bgm = this.scene.sound.add('encounter_bgm', { volume: 0.1 });
                this.scene.bgm.play();
            }).catch((error) => {
                DebugHelper.warn('随机遇敌 BGM 加载失败', error);
            });
            
            DebugHelper.log(`🚨 随机遇敌！速度: ${currentSpeed.toFixed(1)}, 概率: ${(adjustedChance * 100).toFixed(2)}%`);
            this.triggerBattle();
        }
    }
    
    savePlayerPosition() {
        if (this.scene.playerSprite) {
            const tileSize = GAME_CONSTANTS.TILE_SIZE;
            const playerX = this.scene.playerSprite.x;
            const playerY = this.scene.playerSprite.y;

            GameStateStore.patchPlayer({
                x: playerX,
                y: playerY,
                tileX: Math.round((playerX - tileSize / 2) / tileSize),
                tileY: Math.round((playerY - tileSize / 2) / tileSize)
            });
        }
    }

    /**
     * 为远程队员构建战斗数据（不共用队长的完整 player 对象）
     * @param {Object} fallbackData - 本地队长数据（缺省值）
     * @param {Object|null} remoteData - networkManager 中的远程玩家数据
     * @param {string} displayName
     * @returns {Object}
     */
    buildRemoteMemberBattleData(fallbackData, remoteData, displayName = '玩家') {
        const combatKeys = [
            'hp', 'maxHp', 'attack', 'defense', 'speed', 'level', 'exp', 'nextLevelExp',
            'cost', 'maxCost', 'money', 'skills', 'equipment', 'inventory', 'inVehicle', 'vehicle'
        ];

        const memberPlayerData = {
            element: remoteData?.element || fallbackData?.element,
            playerName: remoteData?.playerName || displayName,
            name: remoteData?.playerName || displayName,
            battle: remoteData?.battle || fallbackData?.battle,
            class: remoteData?.class || fallbackData?.class
        };

        combatKeys.forEach((key) => {
            if (remoteData && remoteData[key] !== undefined && remoteData[key] !== null) {
                memberPlayerData[key] = remoteData[key];
            } else if (fallbackData && fallbackData[key] !== undefined) {
                memberPlayerData[key] = fallbackData[key];
            }
        });

        return memberPlayerData;
    }

    buildBattleData(playerData, enemies, encounterType, bgm = null) {
        const battleData = {
            player: playerData,
            enemies: enemies,
            encounterType: encounterType
        };
        
        if (bgm) {
            battleData.bgm = bgm;
        }
        
        if (this.scene.isMultiplayer && this.scene.teamManager && this.scene.teamManager.getTeamId()) {
            battleData.isTeamBattle = true;
            const teamMembers = this.scene.teamManager.getTeamMembers();
            
            // 确保包含队长自己（如果队长不在列表中）
            const networkManager = this.scene.networkManager || window.networkManager;
            const localPlayerId = networkManager ? networkManager.playerId : null;
            const localPlayerName = networkManager ? networkManager.playerName : '玩家';
            
            // 构建完整的队伍成员列表
            let allTeamMembers = [...teamMembers];
            
            // 检查队长是否在列表中
            const hasLeader = allTeamMembers.some(m => m.playerId === localPlayerId);
            if (!hasLeader && localPlayerId && this.scene.teamManager.isTeamLeader()) {
                // 队长不在列表中，添加队长
                allTeamMembers.unshift({
                    playerId: localPlayerId,
                    playerName: localPlayerName
                });
            }
            
            // 为每个队员分配正确的玩家数据
            battleData.teamMembers = allTeamMembers.map(member => {
                // 如果是本地玩家，使用最新的玩家数据
                if (member.playerId === localPlayerId) {
                    return {
                        playerId: member.playerId,
                        playerName: member.playerName || localPlayerName,
                        playerData: playerData
                    };
                } else {
                    const remotePlayerData = networkManager.getPlayer(member.playerId);
                    const memberPlayerData = this.buildRemoteMemberBattleData(
                        playerData,
                        remotePlayerData,
                        member.playerName || remotePlayerData?.playerName || '玩家'
                    );

                    return {
                        playerId: member.playerId,
                        playerName: member.playerName || remotePlayerData?.playerName || '玩家',
                        playerData: memberPlayerData
                    };
                }
            });
            
            battleData.teamBattleData = {
                teamId: this.scene.teamManager.getTeamId(),
                isLeader: this.scene.teamManager.isTeamLeader()
            };
            
            DebugHelper.log('构建战斗数据 - 队伍成员:', battleData.teamMembers.map(m => ({
                playerId: m.playerId,
                playerName: m.playerName
            })));
        }
        
        battleData.previousSceneKey = this.scene.scene.key;

        if (this.scene.isMultiplayer && this.scene.networkManager && playerData) {
            publishCombatState(this.scene.networkManager, playerData);
        }

        return battleData;
    }

    triggerBattle() {
        if (!this.scene.playerSprite) return;

        const allEnemies = this.getRandomEncounterEnemies();
        if (!allEnemies || allEnemies.length === 0) {
            DebugHelper.warn('随机遇敌：没有可用的敌人列表');
            return;
        }
        
        const enemyCount = Math.floor(Math.random() * 3) + 1;
        const selectedEnemies = this.selectRandomEnemies(allEnemies, enemyCount);
        
        DebugHelper.log(`随机遇敌：选择了 ${enemyCount} 个敌人`, selectedEnemies.map(e => e.name));

        // 优先使用当前场景的玩家数据（包含最新的HP值），如果没有则使用gameState
        const latestPlayerData = this.scene.player || GameStateStore.getPlayer();
        if (!latestPlayerData) {
            DebugHelper.error('triggerBattle: 无法获取玩家数据');
            return;
        }
        const playerData = {
            ...latestPlayerData,
            equipment: latestPlayerData.equipment || []
        };

        this.savePlayerPosition();
        const battleData = this.buildBattleData(playerData, selectedEnemies, 'random');
        
        // 如果是组队模式且是队长，通知队伍成员
        if (this.scene.isMultiplayer && this.scene.teamManager && this.scene.teamManager.getTeamId() && this.scene.teamManager.isTeamLeader()) {
            if (this.scene.networkManager && this.scene.networkManager.isConnected()) {
                DebugHelper.log('队长触发战斗，通知队伍成员', battleData);
                this.scene.networkManager.notifyTeamBattleStart(battleData);
            }
        }
        
        this.scene.movementEnabled = false;
        this.scene.scene.pause(this.scene.scene.key);
        this.scene.scene.launch('BattleScene', battleData);
    }
    
    triggerEnemyBattle(enemyNPC) {
        if (this.isTriggeringBattle) {
            return;
        }
        
        if (!this.scene.playerSprite || !enemyNPC || !enemyNPC.data.enemyData) {
            DebugHelper.warn('triggerEnemyBattle: 参数无效');
            return;
        }
        
        const battleScene = this.scene.scene.get('BattleScene');
        if (battleScene && battleScene.scene && battleScene.scene.isActive()) {
            return;
        }
        
        this.isTriggeringBattle = true;
        
        const enemyData = enemyNPC.data.enemyData;
        const battleBgm = enemyNPC.data.bgm;
        
        const enemyForBattle = {
            id: enemyData.id || IdGenerator.generateEnemyId(),
            name: enemyData.name,
            hp: enemyData.maxHp,
            maxHp: enemyData.maxHp,
            attack: enemyData.attack,
            defense: enemyData.defense,
            exp: enemyData.exp,
            money: enemyData.money,
            drops: enemyData.drops || []
        };
        
        // 优先使用当前场景的玩家数据（包含最新的HP值），如果没有则使用gameState
        const latestPlayerData = this.scene.player || GameStateStore.getPlayer();
        
        if (!latestPlayerData) {
            DebugHelper.error('triggerEnemyBattle: 无法获取玩家数据');
            this.isTriggeringBattle = false;
            return;
        }
        
        const playerData = {
            ...latestPlayerData,
            equipment: latestPlayerData.equipment || []
        };

        this.savePlayerPosition();
        const battleData = this.buildBattleData(playerData, [enemyForBattle], 'fixed', battleBgm);

        // 如果是组队模式且是队长，通知队伍成员
        if (this.scene.isMultiplayer && this.scene.teamManager && this.scene.teamManager.getTeamId() && this.scene.teamManager.isTeamLeader()) {
            if (this.scene.networkManager && this.scene.networkManager.isConnected()) {
                DebugHelper.log('队长触发战斗，通知队伍成员', battleData);
                this.scene.networkManager.notifyTeamBattleStart(battleData);
            }
        }

        if (this.scene.bgm) {
            this.scene.bgm.stop();
        }
        
        playSceneSfx(this.scene, 'attck_bgm', { loop: false, volume: 0.1 });

        this._beginFixedBattleIntro(battleData);
        
        DebugHelper.log(`触发固定敌人战斗: ${enemyData.name}`);
    }
    
    /**
     * 获取随机遇敌的敌人列表
     * 优先从地图JSON的randomEnemies字段读取，如果没有则回退到原来的逻辑
     */
    getRandomEncounterEnemies() {
        if (this.scene.isChunkedWorld && this.scene.chunkManager) {
            const enemies = this.scene.chunkManager.getRandomEnemiesAtSprite(this.scene.playerSprite);
            if (enemies.length > 0) {
                DebugHelper.log('从当前区块 randomEnemies 读取随机遇敌列表');
                return this.getEnemiesFromNames(enemies);
            }
        }

        const worldMapJson = this.scene.worldMapJson;
        if (worldMapJson && worldMapJson.randomEnemies && Array.isArray(worldMapJson.randomEnemies)) {
            DebugHelper.log('从地图JSON的randomEnemies字段读取随机遇敌列表');
            return this.getEnemiesFromNames(worldMapJson.randomEnemies);
        }
        
        // 回退到原来的逻辑：根据场景类型确定区域
        const area = this.determineEnemyArea();
        DebugHelper.log(`使用默认区域 ${area} 的敌人列表`);
        return this.getFreshEnemies(area);
    }
    
    /**
     * 根据敌人名称或ID列表从enemies.json中查找对应的敌人数据
     */
    getEnemiesFromNames(enemyNames) {
        if (!this.enemies || !Array.isArray(enemyNames)) {
            return [];
        }
        
        const foundEnemies = [];
        
        // 遍历所有区域查找敌人
        for (const area in this.enemies) {
            const areaEnemies = this.enemies[area];
            if (!Array.isArray(areaEnemies)) continue;
            
            for (const enemyName of enemyNames) {
                // 支持按名称或ID查找
                const enemy = areaEnemies.find(e => 
                    e.name === enemyName || e.id === enemyName
                );
                
                if (enemy && !foundEnemies.find(e => e.name === enemy.name)) {
                    foundEnemies.push({
                        id: enemy.id,
                        name: enemy.name,
                        hp: enemy.maxHp,
                        maxHp: enemy.maxHp,
                        attack: enemy.attack,
                        defense: enemy.defense,
                        exp: enemy.exp,
                        money: enemy.money,
                        drops: enemy.drops || []
                    });
                }
            }
        }
        
        if (foundEnemies.length === 0) {
            DebugHelper.warn(`未找到指定的敌人: ${enemyNames.join(', ')}`);
        }
        
        return foundEnemies;
    }
    
    /**
     * 根据场景类型确定敌人区域
     */
    determineEnemyArea() {
        const sceneConfig = this.scene.worldMapJson?.sceneConfig;
        const area = getEncounterArea(sceneConfig, this.scene.scene.key);
        return area || 'world';
    }

    getEncounterConfig() {
        if (this.scene.isChunkedWorld && this.scene.chunkManager) {
            return this.scene.chunkManager.getEncounterConfigAtSprite(this.scene.playerSprite);
        }
        return this.scene.worldMapJson?.sceneConfig || {};
    }
    
    getFreshEnemies(area) {
        if (!this.enemies || !this.enemies[area]) {
            DebugHelper.warn(`区域 ${area} 没有敌人数据`);
            return [];
        }
        
        const originalEnemies = this.enemies[area];
        
        return originalEnemies.map(enemy => ({
            id: enemy.id,
            name: enemy.name,
            hp: enemy.maxHp,
            maxHp: enemy.maxHp,
            attack: enemy.attack,
            defense: enemy.defense,
            exp: enemy.exp,
            money: enemy.money,
            drops: enemy.drops || []
        }));
    }
    
    selectRandomEnemies(enemies, count) {
        if (!enemies || enemies.length === 0) {
            return [];
        }
        
        if (count >= enemies.length) {
            return [...enemies];
        }
        
        const shuffled = [...enemies];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        
        return shuffled.slice(0, count);
    }
}

